import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';

import { Observable, Subscription, MonoTypeOperatorFunction } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';

export type FormControls<T> = { [K in keyof T]: UntypedFormControl };

export abstract class PartialForm<Data, Result> {
  protected subscription = new Subscription();

  form: UntypedFormGroup;
  value$: Observable<Data>;

  get value(): Data {
    return this.form.getRawValue() as Data;
  }

  saving = false;
  saved = false;

  get enabled() { return this.form.enabled }
  get disabled() { return this.form.disabled }

  enable() { this.form.enable() }
  disable() { this.form.disable() }

  get valid() { return this.form.valid }
  get invalid() { return this.form.invalid }
  get pending() { return this.form.pending }

  // should this form be saved when submitting a batch?
  // is it valid, unsaved and enabled?
  get submittable(): boolean {
    return this.enabled && this.valid && !this.saved;
  }

  initialize() {
    this.form = this.build();
    this.value$ = this.form.valueChanges as Observable<Data>;
  }

  submit(): Observable<Result> {
    this.saving = true;
    return this.save(this.value).pipe(
      finalize(() => { this.saving = false })
    );
  }

  destroy(): void {
    this.subscription.unsubscribe();
  }

  // return a form group that conforms to the shape of Data
  protected abstract build(): UntypedFormGroup;

  // given the value from the form, save it somewhere and return Result
  protected abstract save(value: Data): Observable<Result>;

  // if there are error messages from the server that match the key of a control
  // apply those messages to the control, so they show up in error.
  // 'base' messages are applied to the form
  //
  // they can be selected with control.getErrors('serverError')
  protected applyErrorsToForm<T>(): MonoTypeOperatorFunction<T> {
    return tap<T>(null, (e) => {
      if (e.errors) {
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const key in this.form.controls) {
          const errors = e.errors[key];
          if (errors) {
            const messages = Array.isArray(errors) ? errors : [errors];
            this.form.controls[key].setErrors({ serverError: messages.join(', ') });
          }
        }

        if (e.errors.base) {
          this.form.setErrors({ serverError: e.errors.base });
        }
      }
    });
  }

  // mark the form as having been successfully saved, and 'lock' the form out
  protected markSuccessfulSave<T>(): MonoTypeOperatorFunction<T> {
    return tap<T>(() => { this.saved = true });
  }

  /**
   * Use on build step to prevent issue with previously destoyed subscription.
   * Currently only applies to InsuranceForm where we add things to the subscription
   * on the build step.
   */
  resetSubscription(): void {
    this.destroy();
    this.subscription = new Subscription();
  }
}
